import { BlobAlreadyExistsError, UploadHttpError, type UploadTransport } from "./transport";
import { markCompleted } from "./resume";
import type { FileTask, UploadStats } from "./types";

/**
 * Browsers cap concurrent connections at ~6 per host. Five leaves one slot free
 * so a manifest write never queues behind a long image upload.
 */
const CONCURRENCY = 5;
const MAX_ATTEMPTS = 4;
const BASE_BACKOFF_MS = 800;

/** HTTP statuses worth retrying. 403-overwrite is handled separately as success. */
function isRetryable(err: unknown): boolean {
  if (err instanceof UploadHttpError) {
    return err.status === 408 || err.status === 429 || err.status >= 500;
  }
  // Network failures and timeouts surface as plain Errors.
  return !(err instanceof DOMException && err.name === "AbortError");
}

function backoffMs(attempt: number): number {
  const exp = BASE_BACKOFF_MS * 2 ** (attempt - 1);
  return Math.min(exp, 15_000) + Math.random() * 400; // jitter avoids retry convoys
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export interface UploaderEvents {
  onStats: (stats: UploadStats) => void;
  onTaskChange: (task: FileTask) => void;
  onDone: (tasks: FileTask[]) => void;
}

export class Uploader {
  private readonly controller = new AbortController();
  private cursor = 0;
  private inFlight = 0;
  private running = false;
  private paused = false;

  /** Trailing samples of [timestampMs, bytesDone] used for throughput and ETA. */
  private samples: Array<[number, number]> = [];
  private lastEmit = 0;

  constructor(
    private readonly tasks: FileTask[],
    private readonly transport: UploadTransport,
    private readonly sessionId: string,
    private readonly events: UploaderEvents
  ) {}

  get aborted(): boolean {
    return this.controller.signal.aborted;
  }

  abort(): void {
    this.paused = true;
    this.controller.abort();
  }

  async start(): Promise<void> {
    if (this.running) return;
    this.running = true;
    this.emitStats(true);

    const workers = Array.from({ length: Math.min(CONCURRENCY, this.tasks.length) }, () =>
      this.worker()
    );
    await Promise.all(workers);

    this.running = false;
    this.emitStats(true);
    this.events.onDone(this.tasks);
  }

  private nextTask(): FileTask | null {
    while (this.cursor < this.tasks.length) {
      const t = this.tasks[this.cursor++];
      if (t.status === "pending") return t;
    }
    return null;
  }

  private async worker(): Promise<void> {
    for (;;) {
      if (this.paused) return;
      const task = this.nextTask();
      if (!task) return;
      await this.runTask(task);
    }
  }

  private async runTask(task: FileTask): Promise<void> {
    this.inFlight++;
    task.status = "uploading";
    task.loaded = 0;
    this.events.onTaskChange(task);

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      task.attempts = attempt;
      try {
        await this.transport.put({
          blobPath: task.blobPath,
          body: task.file,
          contentType: task.file.type || "application/octet-stream",
          signal: this.controller.signal,
          onProgress: (loaded) => {
            task.loaded = loaded;
            this.emitStats(false);
          },
        });
        await this.settle(task, "done");
        return;
      } catch (err) {
        if (err instanceof BlobAlreadyExistsError) {
          // A previous attempt reached Azure; the response was just lost.
          await this.settle(task, "skipped");
          return;
        }
        if (this.controller.signal.aborted) {
          task.status = "pending"; // requeued if the user resumes
          task.loaded = 0;
          this.inFlight--;
          this.events.onTaskChange(task);
          return;
        }
        if (attempt === MAX_ATTEMPTS || !isRetryable(err)) {
          task.status = "failed";
          task.error = err instanceof Error ? err.message : String(err);
          task.loaded = 0;
          this.inFlight--;
          this.events.onTaskChange(task);
          this.emitStats(true);
          return;
        }
        task.loaded = 0;
        await sleep(backoffMs(attempt));
      }
    }
  }

  private async settle(task: FileTask, status: "done" | "skipped"): Promise<void> {
    task.status = status;
    task.loaded = task.file.size;
    task.error = undefined;
    this.inFlight--;
    try {
      await markCompleted(this.sessionId, task.key);
    } catch {
      // Resume becomes less useful, but the upload itself succeeded - don't fail it.
    }
    this.events.onTaskChange(task);
    this.emitStats(true);
  }

  /** Recomputes aggregate stats. Throttled to ~4 Hz unless forced. */
  private emitStats(force: boolean): void {
    const now = Date.now();
    if (!force && now - this.lastEmit < 250) return;
    this.lastEmit = now;

    let done = 0;
    let skipped = 0;
    let failed = 0;
    let bytesTotal = 0;
    let bytesDone = 0;

    for (const t of this.tasks) {
      bytesTotal += t.file.size;
      if (t.status === "done") {
        done++;
        bytesDone += t.file.size;
      } else if (t.status === "skipped") {
        skipped++;
        bytesDone += t.file.size;
      } else if (t.status === "failed") {
        failed++;
      } else if (t.status === "uploading") {
        bytesDone += t.loaded;
      }
    }

    this.samples.push([now, bytesDone]);
    // Keep a 20 s trailing window so the rate reflects current conditions.
    while (this.samples.length > 2 && now - this.samples[0][0] > 20_000) this.samples.shift();

    let bytesPerSecond: number | null = null;
    if (this.samples.length >= 2) {
      const [t0, b0] = this.samples[0];
      const dt = (now - t0) / 1000;
      if (dt >= 2) bytesPerSecond = Math.max(0, (bytesDone - b0) / dt);
    }

    const remaining = bytesTotal - bytesDone;
    const etaSeconds =
      bytesPerSecond && bytesPerSecond > 1024 ? Math.round(remaining / bytesPerSecond) : null;

    this.events.onStats({
      total: this.tasks.length,
      done,
      skipped,
      failed,
      inFlight: this.inFlight,
      bytesTotal,
      bytesDone,
      bytesPerSecond,
      etaSeconds,
    });
  }
}
